"use client";

// Общий контент/данные для всех кандидатов главной (входной группы).
// Парадигмы (Шёлк/Мягкий/Спотлайт/Синтвейв/Свисс/Аврора-стекло) импортируют
// отсюда одни и те же тексты и хук данных — меняется только «оболочка».

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Department } from "@/lib/types";
import {
  Megaphone, LineChart, FlaskConical, Wallet, Layers, Boxes, Rocket, Truck, Send, BarChart3,
  Trophy, Receipt, ListChecks, RefreshCw, ShieldCheck, Scale, CalendarRange, MoonStar, type LucideIcon,
} from "lucide-react";
import { IconStar, IconLabel, IconBot } from "@/components/ui/icons";
// DEPT_ICON/fallbackDeptIcon — из ЕДИНОГО реестра иконок (lib/module-icons),
// тот же источник читает навигация (app-shell) и панель отдела.
import { DEPT_ICON, fallbackDeptIcon, type IconType } from "@/lib/module-icons";

export { DEPT_ICON, fallbackDeptIcon };

export type HomeFeature = { icon: LucideIcon; title: string; desc: string };
export type HomeSection = {
  id: string; eyebrow: string; title: string; lead: string; accent: string;
  icon: LucideIcon; route: string; cta: string; features: HomeFeature[];
};

export const SECTIONS: HomeSection[] = [
  {
    id: "assortment", eyebrow: "Старт продукта", title: "Матрица и запуск",
    lead: "Ассортиментная матрица, поиск новинок и запуск товара — единая воронка от идеи до согласования к закупке.",
    accent: "#8b7bff", icon: Boxes, route: "/modules/assortment", cta: "Открыть матрицу",
    features: [
      { icon: Boxes, title: "Ассортиментная матрица", desc: "Дашборд, поиск новинок, воронка и окончательный юнит по странам." },
      { icon: Rocket, title: "Запуск продукта", desc: "Пайплайн вывода товара с фото, этапами и контролем готовности." },
    ],
  },
  {
    id: "rd", eyebrow: "Разработка и логистика", title: "R&D · Коллекция",
    lead: "Себестоимость в валюте, приёмки и движение рейсов — от закупа до поставки на склад в одном месте.",
    accent: "#22d3ee", icon: FlaskConical, route: "/modules/purchases", cta: "Открыть коллекцию",
    features: [
      { icon: Wallet, title: "Финансы и валюты", desc: "Себестоимость заказа в валюте поставщика, курсы МойСклад и ЦБ." },
      { icon: Truck, title: "Логистика рейсов", desc: "Канбан движения рейсов с drag-and-drop и авто-карточкой из приёмки." },
      { icon: Layers, title: "Приёмки и брак", desc: "Учёт приёмок по позициям, отметки брака и связка с закупом." },
    ],
  },
  {
    id: "marketing", eyebrow: "Трафик и креаторы", title: "Маркетинг",
    lead: "Отчёты креаторов: сбор через Telegram-бот и авто-метрики из Instagram, TikTok, YouTube и ВКонтакте — охваты, вовлечённость и топы в одном дашборде.",
    accent: "#ff6ec7", icon: Megaphone, route: "/departments/marketing", cta: "Открыть отчёты",
    features: [
      { icon: Send, title: "Сбор через Telegram-бот", desc: "Креаторы сдают отчёты ботом, метрики подтягиваются автоматически." },
      { icon: BarChart3, title: "Метрики из соцсетей", desc: "Instagram, TikTok, YouTube и ВКонтакте через Apify: просмотры, лайки, ER." },
      { icon: Trophy, title: "Топы и охваты", desc: "Рейтинг креаторов и публикаций, охваты по платформам и по дням." },
    ],
  },
  {
    id: "sales", eyebrow: "Аналитика и финансы", title: "Sales / Аналитика",
    lead: "Дашборд, ОПиУ, юнит-экономика и чек-лист WB — сведено с эталоном 10X до рубля и обновляется автоматически.",
    accent: "#a3e635", icon: LineChart, route: "/departments/sales", cta: "Открыть дашборд",
    features: [
      { icon: Receipt, title: "ОПиУ и финансы", desc: "Отчёт о прибылях и убытках, P&L по месяцам и по SKU, себестоимость." },
      { icon: LineChart, title: "Юнит-экономика", desc: "Маржинальность по SKU, цены и скидки, реклама и ДРР по окнам." },
      { icon: ListChecks, title: "Чек-лист и дашборд", desc: "Посуточный чек-лист по SKU и сводная картина по всем кабинетам WB." },
    ],
  },
];

export const FLOW = ["Матрица", "Коллекция", "Планирование", "Продажи"];

export const FEATURES: HomeFeature[] = [
  { icon: Layers, title: "Все данные в одном месте", desc: "WB, МойСклад, Google Sheets и эталон 10X сведены и обновляются автоматически." },
  { icon: Scale, title: "Сверка методик расчёта", desc: "ОПиУ, юнит-экономика и чек-листы сведены с эталоном — до рубля." },
  { icon: RefreshCw, title: "Авто-синхронизация WB", desc: "Продажи, заказы, остатки и финансовые отчёты подтягиваются по расписанию." },
  { icon: CalendarRange, title: "Планирование и прогноз", desc: "План сезона и месяца по SKU с расчётом прибыли и поставок." },
  { icon: ShieldCheck, title: "Доступ по ролям и отделам", desc: "Каждый видит своё; права настраиваются администратором." },
  { icon: MoonStar, title: "Тёмная и светлая темы", desc: "Работай как удобно — быстрый интерфейс в любое время." },
];

// Иконки утилит совпадают с MODULE_ICON (единый реестр): один модуль — одна иконка.
export const UTILITIES: { icon: IconType; title: string; desc: string; route: string }[] = [
  { icon: IconStar, title: "WB Reviews", desc: "Отзывы и вопросы: автопубликация ответов и аналитика.", route: "/modules/reviews" },
  { icon: IconLabel, title: "Label Maker", desc: "Генерация этикеток и честных знаков под печать.", route: "/modules/labels" },
  { icon: IconBot, title: "Telegram-боты", desc: "Сценарии, заявки и уведомления через ботов компании.", route: "/modules/bots" },
];

export const FAQ: { q: string; a: string }[] = [
  { q: "Что такое модуль?", a: "Модуль — это отдельный раздел портала под конкретную задачу: аналитика, планирование, коллекция и т.д. Модули собраны по отделам и доступны из бокового меню." },
  { q: "Откуда берутся данные?", a: "Данные подтягиваются из Wildberries (API кабинетов), МойСклад, Google Sheets и эталонных листов «Система 10X». Часть показателей сверена с эталоном до рубля." },
  { q: "Как часто обновляется?", a: "Продажи, заказы и остатки WB подтягиваются по расписанию. Финансовые отчёты пересчитываются по мере того, как их присылает WB." },
  { q: "Кому доступны разделы?", a: "Доступ выдаётся по ролям и отделам: сотрудник видит свой отдел, руководитель и администратор — расширенный набор. Настраивается в разделе «Настройки»." },
];

export type HomeMetric = { value: string; label: string };

export function useHomeData() {
  const [depts, setDepts] = useState<Department[]>([]);
  const [stats, setStats] = useState({ modules: 14, departments: 5 });
  useEffect(() => {
    Promise.all([api.getModules(), api.getDepartments()])
      .then(([mods, ds]) => {
        const active = ds.filter((d) => d.key !== "system");
        setDepts(active);
        setStats({ modules: mods.filter((m) => m.status === "active").length || mods.length, departments: active.length });
      })
      .catch(() => {});
  }, []);
  const metrics: HomeMetric[] = [
    { value: `${stats.modules}`, label: "рабочих модулей" },
    { value: `${stats.departments}`, label: "отделов компании" },
    { value: "2", label: "кабинета WB на синке" },
    { value: "10X", label: "сверка по эталону" },
  ];
  return { depts, stats, metrics };
}
